import React, { useMemo } from 'react';
import classnames from 'classnames';
import { useValueChange } from '@leverege/ui-hooks';
import { NumericInput, Checkbox, TabPane, Selector, TextInput, PropertyGrid, Popup, Text } from '@leverege/ui-elements';
import { ModelTypeSelector } from '@leverege/ui-plugin';
import I18N from '@leverege/i18n';
import { Colorizers, Attributes } from '@leverege/ui-attributes';
import Model from "./GeoPointModel.js";
import S from './Grid.css';
import LineStyleSelector from "../../shared/views/LineStyleSelector.js";
import JoinSelector from "../../shared/views/JoinSelector.js";
import Symbolizers from "../../shared/Symbolizers.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t,
  tIcon
} = I18N.ns('molten.dataViewers.map.GeoPointEditor');
const SHOW_ACCURACY_ON_OPTS = [{
  value: null,
  name: 'Never'
}, {
  value: 'selected',
  name: 'Selected'
}, {
  value: 'rollover',
  name: 'Rollover'
}, {
  value: 'always',
  name: 'Always'
}];
function GeoPointEditor(props) {
  const {
    value
  } = props;
  const onModelChange = useValueChange(Model, props);
  const sizeUnits = useMemo(() => {
    return [{
      name: t('pixels'),
      value: 'pixels'
    }, {
      name: t('meters'),
      value: 'meters'
    }, {
      name: t('common'),
      value: 'common'
    }];
  }, []);
  if (value == null) {
    return null;
  }
  const attrName = Model.getAttributeName(value);
  const objectType = Model.getObjectType(value);
  const showAccuracy = Model.getShowAccuracyOn(value);
  const hasOverrideIconSize = Model.getHasOverrideIconSize(value);
  const attribute = Attributes.getAttribute(attrName, objectType);
  return /*#__PURE__*/_jsxs(TabPane, {
    children: [/*#__PURE__*/_jsx(TabPane.Tab, {
      tabId: "point",
      tabName: "Point",
      children: /*#__PURE__*/_jsxs(PropertyGrid, {
        variant: "geoPointEditor|editor",
        children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          label: /*#__PURE__*/_jsx(Checkbox, {
            containerClassName: S.fill,
            label: t('visible'),
            eventData: "setVisible",
            value: Model.getVisible(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('showZoom'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            eventData: "setShowAtZoom",
            min: 0,
            max: Math.min(25, Model.getHideAtZoom(value)),
            allowError: true,
            value: Model.getShowAtZoom(value),
            onChange: onModelChange
          }, `haz-${Model.getHideAtZoom(value) || 'n'}`)
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('hideZoom'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: Math.max(0, Model.getShowAtZoom(value)),
            max: 25,
            eventData: "setHideAtZoom",
            allowError: true,
            value: Model.getHideAtZoom(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          label: /*#__PURE__*/_jsx(Checkbox, {
            label: t('hasOverrideIconSize'),
            eventData: "setHasOverrideIconSize",
            value: hasOverrideIconSize,
            onChange: onModelChange
          })
        }), hasOverrideIconSize && [/*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('overrideIconSize'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            float: true,
            eventData: "setOverrideIconSize",
            value: Model.getOverrideIconSize(value),
            onChange: onModelChange
          })
        }, "ois"), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('overrideIconSizeUnits'),
          children: /*#__PURE__*/_jsx(Selector, {
            eventData: "setOverrideIconSizeUnits",
            value: Model.getOverrideIconSizeUnits(value),
            values: sizeUnits,
            onChange: onModelChange
          })
        }, "oisu")], /*#__PURE__*/_jsx(PropertyGrid.Item, {
          children: /*#__PURE__*/_jsx(Popup, {
            icon: tIcon('helpIcon', 'fa fa-question-circle fa-fw'),
            children: /*#__PURE__*/_jsxs("div", {
              className: S.pad,
              children: [/*#__PURE__*/_jsx(Text, {
                className: classnames(S.fill, S.hint),
                variant: "caption",
                children: t('settingZoomDescription')
              }), /*#__PURE__*/_jsx(Text, {
                className: S.fill,
                variant: "subtitle",
                children: t('zoomLevel')
              }), /*#__PURE__*/_jsxs("div", {
                className: S.grid,
                style: {
                  columnGap: 'var( --size-padXLarge, 32px)'
                },
                children: [/*#__PURE__*/_jsx("span", {
                  children: "0"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('0Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "3"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('3Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "4"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('4Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "6"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('6Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "10"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('10Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "15"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('15Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "18"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('18Zoom')
                })]
              })]
            })
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Header, {
          title: t('interaction')
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          label: /*#__PURE__*/_jsx(Checkbox, {
            containerClassName: S.fill,
            label: t('rollover'),
            eventData: "setRolloverable",
            value: Model.getRolloverable(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          label: /*#__PURE__*/_jsx(Checkbox, {
            containerClassName: S.fill,
            label: t('selectable'),
            eventData: "setSelectable",
            value: Model.getSelectable(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('startInteractionAtZoom'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            eventData: "setStartInteractionAtZoom",
            min: 0,
            max: Math.min(25, Model.getStartInteractionAtZoom(value)),
            allowError: true,
            value: Model.getStartInteractionAtZoom(value),
            onChange: onModelChange
          }, `stiaz-${Model.getStopInteractionAtZoom(value) || 'n'}`)
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('stopInteractionAtZoom'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: Math.max(0, Model.getStartInteractionAtZoom(value)),
            max: 25,
            eventData: "setStopInteractionAtZoom",
            allowError: true,
            value: Model.getStopInteractionAtZoom(value),
            onChange: onModelChange
          }, `spiaz-${Model.getStartInteractionAtZoom(value) || 'n'}`)
        }), /*#__PURE__*/_jsx(PropertyGrid.Header, {
          title: t('appearance')
        }), ModelTypeSelector.createSelectorAndEditor({
          editorProps: {
            ...props,
            objectType
          },
          modelKey: 'symbolizer',
          values: Symbolizers.getSymbolizersFor(objectType),
          value: Model.getSymbolizer(value),
          eventData: 'setSymbolizer',
          onChange: onModelChange,
          label: t('symbol'),
          isPropertyGrid: true
        }), /*#__PURE__*/_jsx(PropertyGrid.Header, {
          title: t('scale')
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('normal'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: 0.1,
            max: 10,
            float: true,
            eventData: "setNormalScale",
            value: Model.getNormalScale(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('rollover'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: 0.1,
            max: 10,
            float: true,
            eventData: "setRolloverScale",
            value: Model.getRolloverScale(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('selected'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: 0.1,
            max: 10,
            float: true,
            eventData: "setSelectedScale",
            value: Model.getSelectedScale(value),
            onChange: onModelChange
          })
        })]
      })
    }), attribute?.source?.attribute?.metadata?.accuracy && /*#__PURE__*/_jsx(TabPane.Tab, {
      tabId: "accuracy",
      tabName: t('accuracy'),
      children: /*#__PURE__*/_jsxs(PropertyGrid, {
        variant: "mapLayerEditor",
        children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('showAccuracyOn'),
          children: /*#__PURE__*/_jsx(Selector, {
            value: showAccuracy,
            onChange: onModelChange,
            eventData: "setShowAccuracyOn",
            values: SHOW_ACCURACY_ON_OPTS
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Header, {
          title: t('fill')
        }), /*#__PURE__*/_jsxs(PropertyGrid, {
          inset: true,
          variant: "inset",
          children: [ModelTypeSelector.createSelectorAndEditor({
            modelKey: 'colorizer',
            values: Colorizers.getColorizersFor(objectType),
            value: Model.getFillColor(value),
            eventData: 'setFillColor',
            isPropertyGrid: true,
            onChange: onModelChange,
            label: t('color'),
            editorProps: {
              ...props,
              objectType
            }
          }, S.fill), /*#__PURE__*/_jsx(PropertyGrid.Item, {
            label: t('opacity'),
            children: /*#__PURE__*/_jsx(NumericInput, {
              min: 0,
              max: 1,
              float: true,
              eventData: "setFillOpacity",
              allowError: true,
              value: Model.getFillOpacity(value),
              onChange: onModelChange
            })
          }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
            label: t('pattern'),
            children: /*#__PURE__*/_jsx(TextInput, {
              eventData: "setFillPattern",
              allowError: true,
              value: Model.getFillPattern(value),
              onChange: onModelChange
            })
          })]
        }), /*#__PURE__*/_jsx(PropertyGrid.Header, {
          title: t('outline')
        }), /*#__PURE__*/_jsxs(PropertyGrid, {
          inset: true,
          variant: "inset",
          children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
            label: t('width'),
            children: /*#__PURE__*/_jsx(NumericInput, {
              min: 0,
              max: 50,
              float: true,
              eventData: "setLineWidth",
              allowError: true,
              value: Model.getLineWidth(value),
              onChange: onModelChange
            })
          }), ModelTypeSelector.createSelectorAndEditor({
            modelKey: 'colorizer',
            values: Colorizers.getColorizersFor(objectType),
            value: Model.getLineColor(value),
            eventData: 'setLineColor',
            onChange: onModelChange,
            label: t('color'),
            isPropertyGrid: true,
            editorProps: {
              ...props,
              objectType
            }
          }, S.fill), /*#__PURE__*/_jsx(PropertyGrid.Item, {
            label: t('opacity'),
            children: /*#__PURE__*/_jsx(NumericInput, {
              min: 0,
              max: 1,
              float: true,
              eventData: "setLineOpacity",
              allowError: true,
              value: Model.getLineOpacity(value),
              onChange: onModelChange
            })
          }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
            label: t('style'),
            children: /*#__PURE__*/_jsx(LineStyleSelector, {
              allowCustom: false,
              value: Model.getLineStyle(value),
              eventData: "setLineStyle",
              onChange: onModelChange
            })
          }), Model.getLineStyle(value) === 'pattern' && /*#__PURE__*/_jsx(PropertyGrid.Item, {
            label: t('pattern'),
            children: /*#__PURE__*/_jsx(TextInput, {
              eventData: "setLinePattern",
              allowError: true,
              value: Model.getLinePattern(value),
              onChange: onModelChange
            })
          }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
            label: t('join'),
            children: /*#__PURE__*/_jsx(JoinSelector, {
              eventData: "setLineJoin",
              value: Model.getLineJoin(value) || 'miter',
              onChange: onModelChange
            })
          })]
        })]
      })
    })]
  });
}
export default /*#__PURE__*/React.memo(GeoPointEditor);